import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Calendar,
  Clock,
  Users,
  CheckCircle,
  ArrowRight,
  AlertCircle,
  Mail,
  ExternalLink,
  ClipboardList,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { schedulingUtils, SCHEDULING_CONFIG } from '../../config/scheduling';

interface SmartDemoSchedulerProps {
  /** Custom styling classes */
  className?: string;
  /** Callback when demo is scheduled */
  onDemoScheduled?: () => void;
  /** Source for analytics tracking */
  source?: string;
  /** Whether to show inline calendar or popup window */
  displayMode?: 'popup' | 'inline';
}

export const SmartDemoScheduler: React.FC<SmartDemoSchedulerProps> = ({
  className = '',
  onDemoScheduled,
  source = 'schedule_demo_page',
  displayMode = 'popup',
}) => {
  const navigate = useNavigate();
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const [actuallyCompleted, setActuallyCompleted] = useState(false);

  // Check if they actually completed assessment on mount
  useEffect(() => {
    const completed = schedulingUtils.hasCompletedAssessment();
    setActuallyCompleted(completed);
    if (completed) {
      setHasCompletedAssessment(true);
    }
  }, []);

  const handleAssessmentCheckboxChange = (checked: boolean) => {
    setHasCompletedAssessment(checked);
  };

  const handleCompleteAssessment = () => {
    // Track that they need to complete assessment
    schedulingUtils.trackEvent('assessment_required_from_scheduler', { source });

    // Navigate to universal onboarding page (not assessment directly)
    navigate('/onboarding');

    // Ensure page loads at the top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleScheduleDemo = () => {
    if (!hasCompletedAssessment) {
      return; // Shouldn't happen due to button state, but safety check
    }

    // Track demo scheduling access
    schedulingUtils.trackEvent('demo_scheduled_access', {
      source,
      assessment_completed: actuallyCompleted,
    });

    // Open Google Calendar appointment slots in new window
    const calendarUrl = SCHEDULING_CONFIG.googleCalendar.appointmentSlotsUrl;

    // Open calendar scheduling in new window with optimal size for Google Calendar
    window.open(
      calendarUrl,
      '_blank',
      'width=900,height=750,scrollbars=yes,resizable=yes,location=yes',
    );

    onDemoScheduled?.();
  };

  return (
    <div className={`min-h-screen bg-gray-50 py-16 ${className}`}>
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Calendar className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Schedule Your Demo</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get a personalized 30-minute walkthrough of the Community App platform tailored
            specifically to your community's needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Main Scheduler Card */}
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Demo Scheduling
              </CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Assessment Requirement Notice */}
              <Alert>
                <ClipboardList className="h-4 w-4" />
                <AlertDescription>
                  <strong>To confirm your demo</strong>, please complete our brief community
                  assessment first. This helps us prepare a personalized demonstration focused on
                  your specific needs and challenges.
                </AlertDescription>
              </Alert>

              {/* Assessment Status Check */}
              <div className="space-y-4">
                {actuallyCompleted ? (
                  <div className="flex items-center space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-medium">
                      Assessment completed - you're ready to schedule!
                    </span>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="assessment-completed"
                        checked={hasCompletedAssessment}
                        onCheckedChange={handleAssessmentCheckboxChange}
                      />
                      <label
                        htmlFor="assessment-completed"
                        className="text-sm font-medium text-gray-700"
                      >
                        I have completed the community assessment
                      </label>
                    </div>

                    {!hasCompletedAssessment && (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          The assessment takes 5-10 minutes and helps us understand your community
                          size, challenges, and priorities so we can show you the most relevant
                          features.
                        </AlertDescription>
                      </Alert>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {hasCompletedAssessment ? (
                  displayMode === 'inline' ? (
                    // Inline calendar embed
                    <div className="w-full border rounded-lg overflow-hidden">
                      <iframe
                        src={SCHEDULING_CONFIG.googleCalendar.appointmentSlotsUrl}
                        style={{ border: 0 }}
                        width="100%"
                        height="600"
                        frameBorder="0"
                        title="Schedule Demo"
                      />
                    </div>
                  ) : (
                    // Popup button
                    <Button
                      onClick={handleScheduleDemo}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                      size="lg"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Demo Now
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={handleCompleteAssessment}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                    size="lg"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    Complete Assessment First
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* Demo Details */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold text-gray-900">What to Expect:</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-3">
                    <Clock className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">45-Minute Session</p>
                      <p className="text-gray-600">
                        Live demo tailored to your assessment responses
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Users className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">Expert Walkthrough</p>
                      <p className="text-gray-600">Q&A with our implementation specialists</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">Custom Pricing</p>
                      <p className="text-gray-600">Get pricing specific to your community size</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Panel */}
          <div className="space-y-6">
            {/* Assessment Benefits */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Why the Assessment?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Personalized Demo</p>
                    <p className="text-gray-600 text-sm">
                      We'll focus on features that solve your specific challenges
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Saves Time</p>
                    <p className="text-gray-600 text-sm">
                      No generic demos - we'll come prepared for your needs
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-purple-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">Better Preparation</p>
                    <p className="text-gray-600 text-sm">
                      Our team reviews your responses beforehand
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Next Steps */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What Happens Next</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="min-w-fit">
                    1
                  </Badge>
                  <p className="text-sm text-gray-600">
                    {hasCompletedAssessment
                      ? 'Schedule your demo via email'
                      : 'Complete the community assessment (5-10 mins)'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="min-w-fit">
                    2
                  </Badge>
                  <p className="text-sm text-gray-600">
                    We'll reply within 24 hours with time options
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="min-w-fit">
                    3
                  </Badge>
                  <p className="text-sm text-gray-600">30-minute personalized demo call</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="min-w-fit">
                    4
                  </Badge>
                  <p className="text-sm text-gray-600">
                    Custom proposal and pricing (if interested)
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6 text-center">
                <h3 className="font-semibold text-blue-900 mb-2">Questions?</h3>
                <p className="text-blue-800 text-sm mb-4">Need to speak with someone right away?</p>
                {/* <a 
                  href="mailto:support@example.com"
                  className="text-blue-600 font-semibold hover:text-blue-700 underline"
                >
                  Email support@example.com
                </a> */}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
